import axios from "axios";
import atob from "atob";
import { DateTime } from "luxon";
import { ApplicationApiEndpoint } from "./Constants";
import { sleep } from "./Utilities";

type AnonymousTokenResponse = {
    token: string;
    refreshToken: string;
};

type UserTokenResponse = {
    token: string;
    refreshToken: string;
    id: string;
};

type JwtPayload = {
    exp?: number;
};

const refreshTokenStorageKey = "sf.anonymous.refreshToken";
const userTokenKey = "sf.user.token";
const userRefreshTokenKey = "sf.user.refreshToken";
const userIdKey = "sf.user.id";
const userProfileKey = "sf.user.profile";

export class AuthenticationHelper {
    private token: string | undefined = undefined;
    private refreshToken: string | undefined = undefined;
    private isRetrievingToken = false;

    public constructor() {
        this.token = undefined;
        this.refreshToken = this.getStoredRefreshToken();
    }

    public isAuthenticated = (): boolean => {
        return this.isTokenValid(this.token);
    };

    public getToken = () => this.token ?? "";

    public invalidateToken = (): void => {
        this.token = undefined;
    };

    public fetchTokenAsync = async (forceRefresh = false): Promise<string> => {
        // Prefer a valid user JWT over the anonymous token
        const userToken = this.getStoredValue(userTokenKey);
        if (!forceRefresh && userToken && this.isTokenValid(userToken)) {
            return userToken;
        }

        const userRefreshToken = this.getStoredValue(userRefreshTokenKey);
        if (userRefreshToken && this.isTokenValid(userRefreshToken)) {
            const refreshedUserToken = await this.refreshUserTokenAsync(userRefreshToken);
            if (refreshedUserToken) {
                return refreshedUserToken;
            }
        }

        if (!forceRefresh && this.isTokenValid(this.token)) {
            return this.token!;
        }

        if (this.isRetrievingToken) {
            while (this.isRetrievingToken) {
                await sleep(100);
            }
            return this.fetchTokenAsync();
        }

        this.isRetrievingToken = true;
        try {
            if (this.refreshToken && this.isTokenValid(this.refreshToken)) {
                const refreshedToken = await this.refreshAnonymousTokenAsync();
                if (refreshedToken) {
                    return refreshedToken;
                }
            }

            return await this.issueAnonymousTokenAsync();
        } catch {
            return "";
        } finally {
            this.isRetrievingToken = false;
        }
    };

    private issueAnonymousTokenAsync = async (): Promise<string> => {
        const response = await axios.post<AnonymousTokenResponse>(
            `${ApplicationApiEndpoint}authentication/anonymous-token`
        );
        this.setTokenPair(response.data);
        return this.token ?? "";
    };

    private refreshAnonymousTokenAsync = async (): Promise<string | undefined> => {
        if (!this.refreshToken) {
            return undefined;
        }

        try {
            const response = await axios.post<AnonymousTokenResponse>(
                `${ApplicationApiEndpoint}authentication/anonymous-token/refresh`,
                { refreshToken: this.refreshToken }
            );
            this.setTokenPair(response.data);
            return this.token;
        } catch {
            this.clearTokenPair();
            return undefined;
        }
    };

    private setTokenPair = (payload: AnonymousTokenResponse): void => {
        this.token = payload.token;
        this.refreshToken = payload.refreshToken;
        this.persistRefreshToken(payload.refreshToken);
    };

    private refreshUserTokenAsync = async (refreshToken: string): Promise<string | undefined> => {
        try {
            const response = await axios.post<UserTokenResponse>(
                `${ApplicationApiEndpoint}user/refreshtoken`,
                { refreshToken },
            );
            this.setUserSession(response.data.token, response.data.id, response.data.refreshToken);
            return response.data.token;
        } catch {
            this.clearUserSession();
            return undefined;
        }
    };

    private clearTokenPair = (): void => {
        this.token = undefined;
        this.refreshToken = undefined;
        this.persistRefreshToken(undefined);
    };

    private isTokenValid = (token: string | undefined): boolean => {
        if (!token) {
            return false;
        }

        const payload = this.decodeToken(token);
        if (!payload?.exp) {
            return false;
        }

        const expiry = payload.exp < 1_000_000_000_000 ? payload.exp * 1000 : payload.exp;
            return DateTime.now().toMillis() < expiry;
    };

    private decodeToken = (token: string): JwtPayload | undefined => {
        try {
            const parts = token.split(".");
            if (parts.length < 3 || !parts[1]) {
                return undefined;
            }

            return JSON.parse(atob(parts[1])) as JwtPayload;
        } catch {
            return undefined;
        }
    };

    private getStoredRefreshToken = (): string | undefined => {
        return this.getStoredValue(refreshTokenStorageKey);
    };

    private persistRefreshToken = (value: string | undefined): void => {
        this.persistValue(refreshTokenStorageKey, value);
    };

    // ---------------------------------------------------------------------------
    // User session management
    // ---------------------------------------------------------------------------

    public setUserSession = (token: string, id: string, refreshToken: string): void => {
        this.persistValue(userTokenKey, token);
        this.persistValue(userRefreshTokenKey, refreshToken);
        this.persistValue(userIdKey, id);
        // Invalidate cached anonymous token so next call picks up user JWT
        this.token = undefined;
    };

    public setUserProfile = (profile: { name: string; username: string }): void => {
        this.persistValue(userProfileKey, JSON.stringify(profile));
    };

    public getUserProfile = (): { name: string; username: string } | null => {
        const raw = this.getStoredValue(userProfileKey);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as { name: string; username: string };
        } catch {
            return null;
        }
    };

    public clearUserSession = (): void => {
        this.persistValue(userTokenKey, undefined);
        this.persistValue(userRefreshTokenKey, undefined);
        this.persistValue(userIdKey, undefined);
        this.persistValue(userProfileKey, undefined);
        this.token = undefined;
    };

    public isUserAuthenticated = (): boolean => {
        const userToken = this.getStoredValue(userTokenKey);
        const userRefreshToken = this.getStoredValue(userRefreshTokenKey);
        return (!!userToken && this.isTokenValid(userToken)) || (!!userRefreshToken && this.isTokenValid(userRefreshToken));
    };

    public getUserId = (): string | undefined => {
        return this.isUserAuthenticated() ? (this.getStoredValue(userIdKey) ?? undefined) : undefined;
    };

    // ---------------------------------------------------------------------------
    // Private storage helpers
    // ---------------------------------------------------------------------------

    private getStoredValue = (key: string): string | undefined => {
        if (typeof window === "undefined" || !window.localStorage) {
            return undefined;
        }
        return window.localStorage.getItem(key) ?? undefined;
    };

    private persistValue = (key: string, value: string | undefined): void => {
        if (typeof window === "undefined" || !window.localStorage) {
            return;
        }
        if (!value) {
            window.localStorage.removeItem(key);
            return;
        }
        window.localStorage.setItem(key, value);
    };
}
